import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Badge,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import SidebarResizeHandle from "../../shared/SidebarResizeHandle";
import { LAYERS } from "../constants";
import { entitiesForLayer } from "../portfolioData";
import {
  entityRecord,
  matchesQueryAndFilters,
  postalCodeOf,
  sortEntities,
} from "../helpers";
import EntityList from "./EntityList";
import FiltersModal from "./FiltersModal";
import BrokerDetail from "./BrokerDetail";
import PropertyDetail from "./PropertyDetail";
import MotorDetail from "./MotorDetail";
import styles from "./sideMenu.module.css";

const ORDER_OPTIONS = {
  [LAYERS.broker]: [
    "created:desc",
    "created:asc",
    "properties:desc",
    "properties:asc",
    "motors:desc",
    "motors:asc",
    "combined:desc",
    "combined:asc",
    "insured:desc",
    "insured:asc",
    "premium:desc",
    "premium:asc",
  ],
  [LAYERS.property]: [
    "created:desc",
    "created:asc",
    "insured:desc",
    "insured:asc",
    "premium:desc",
    "premium:asc",
  ],
  [LAYERS.motor]: [
    "created:desc",
    "created:asc",
    "insured:desc",
    "insured:asc",
    "premium:desc",
    "premium:asc",
  ],
};

const ORDER_LABELS = {
  "created:desc": "portfolioExplorer.orderOptions.createdDesc",
  "created:asc": "portfolioExplorer.orderOptions.createdAsc",
  "properties:desc": "portfolioExplorer.orderOptions.propertiesDesc",
  "properties:asc": "portfolioExplorer.orderOptions.propertiesAsc",
  "motors:desc": "portfolioExplorer.orderOptions.motorsDesc",
  "motors:asc": "portfolioExplorer.orderOptions.motorsAsc",
  "combined:desc": "portfolioExplorer.orderOptions.combinedDesc",
  "combined:asc": "portfolioExplorer.orderOptions.combinedAsc",
  "insured:desc": "portfolioExplorer.orderOptions.insuredDesc",
  "insured:asc": "portfolioExplorer.orderOptions.insuredAsc",
  "premium:desc": "portfolioExplorer.orderOptions.premiumDesc",
  "premium:asc": "portfolioExplorer.orderOptions.premiumAsc",
};

const uniqueSorted = (values) =>
  [...new Set(values)].sort((a, b) => a.localeCompare(b));

function SideMenu({
  open,
  onToggle,
  width,
  onResize,
  layer,
  stack,
  onOpen,
  onBack,
  query,
  onQueryChange,
  filters,
  onFiltersChange,
  order,
  onOrderChange,
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || "en";
  const [filtersOpen, setFiltersOpen] = useState(false);
  const selection = stack[stack.length - 1] || null;

  const layerEntities = entitiesForLayer[layer];
  const filterOptions = useMemo(
    () => ({
      postalCodes: uniqueSorted(
        layerEntities.map((entity) => postalCodeOf(entity, layer)),
      ),
      cities: uniqueSorted(layerEntities.map((entity) => entity.city)),
      regions: uniqueSorted(layerEntities.map((entity) => entity.region)),
      countries: uniqueSorted(layerEntities.map((entity) => entity.country)),
    }),
    [layer, layerEntities],
  );

  const visibleEntities = useMemo(() => {
    const matched = layerEntities.filter((entity) =>
      matchesQueryAndFilters(entity, layer, query, filters),
    );
    return sortEntities(matched, layer, order);
  }, [layerEntities, layer, query, filters, order]);

  const activeFilterCount =
    filters.postalCodes.length +
    filters.cities.length +
    filters.regions.length +
    filters.countries.length;

  const searchKey =
    layer === LAYERS.broker
      ? "broker"
      : layer === LAYERS.property
        ? "property"
        : "motor";

  const renderDetail = () => {
    const entity = entityRecord(selection.type, selection.id);
    if (!entity) return null;
    if (selection.type === LAYERS.broker) {
      return (
        <BrokerDetail broker={entity} language={language} onOpen={onOpen} />
      );
    }
    if (selection.type === LAYERS.property) {
      return (
        <PropertyDetail property={entity} language={language} onOpen={onOpen} />
      );
    }
    return <MotorDetail motor={entity} language={language} onOpen={onOpen} />;
  };

  return (
    <div
      className={`${styles.container} ${open ? "" : styles.hidden}`}
      style={{ width }}
    >
      <SidebarResizeHandle onResize={onResize} />
      <IconButton
        onClick={onToggle}
        className={styles.toggleButton}
        aria-label={t("common.hide")}
      >
        {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>
      <div className={styles.content}>
        {selection ? (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{ mb: 1 }}
            >
              {t("portfolioExplorer.back")}
            </Button>
            {renderDetail()}
          </>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder={t(`portfolioExplorer.search.${searchKey}`)}
              sx={{ mt: 2, mb: 1.5 }}
            />
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <Badge
                color="primary"
                badgeContent={activeFilterCount}
                invisible={!activeFilterCount}
              >
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setFiltersOpen(true)}
                >
                  {t("portfolioExplorer.filters")}
                </Button>
              </Badge>
              <TextField
                select
                size="small"
                fullWidth
                label={t("portfolioExplorer.order")}
                value={order}
                onChange={(event) => onOrderChange(event.target.value)}
              >
                {ORDER_OPTIONS[layer].map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(ORDER_LABELS[option])}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              {visibleEntities.length}
            </Typography>
            <EntityList
              entities={visibleEntities}
              layer={layer}
              language={language}
              onOpen={onOpen}
            />
            <FiltersModal
              open={filtersOpen}
              filters={filters}
              options={filterOptions}
              onClose={() => setFiltersOpen(false)}
              onApply={(next) => {
                onFiltersChange(next);
                setFiltersOpen(false);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default SideMenu;
